import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import './App.css';

function App() {
  // Configuración del sorteo (AJUSTABLE)
  const DRAW_DURATION = 2000; // Duración total del sorteo en milisegundos (5 segundos)
  const INTERVAL_TIME = 50; // Velocidad de cambio de nombres en milisegundos (50ms = más rápido)

  // Estados principales
  const [pool, setPool] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWinner, setShowWinner] = useState(false);
  const [winners, setWinners] = useState([]); // Array para almacenar todos los ganadores

  // Referencias para intervalos
  const animationInterval = useRef(null);
  const drawTimeout = useRef(null);

  // Efecto para cargar los datos al montar el componente (solo una vez)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          'https://apiautosymas.mvsradio.com/usersName?authToken=ZCnxo811wAr2dlmpYvGysj6CsDvOrlqi'
        );

        if (!response.ok) {
          throw new Error('Error al cargar los datos');
        }

        const data = await response.json();

        // Filtrar usuarios con nombres entre 4 y 5 palabras y guardar datos completos
        const filteredUsers = data.data.users
          .filter(user => typeof user.nombre === 'string')
          .filter(user => {
            const words = user.nombre.trim().split(/\s+/);
            return words.length > 3 && words.length <= 5;
          })
          .map(user => ({
            nombre: user.nombre,
            correo: user.correo || '',
            id: user.id || ''
          }));

        setPool(filteredUsers);
        setIsLoading(false);
      } catch (err) {
        setError('No se pudieron cargar los nombres. Por favor, verifica tu conexión a internet.');
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []); // Array vacío para ejecutar solo una vez

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
      }
      if (drawTimeout.current) {
        clearTimeout(drawTimeout.current);
      }
    };
  }, []);

  // Función para obtener un usuario aleatorio del pool
  const getRandomUser = (usersList) => {
    const randomIndex = Math.floor(Math.random() * usersList.length);
    return usersList[randomIndex];
  };

  // Función para exportar a Excel
  const handleExportExcel = () => {
    if (winners.length === 0) {
      return; // No hacer nada si no hay ganadores
    }

    // Crear datos para Excel
    const data = winners.map(winner => ({
      'Número': winner.numero,
      'Nombre': winner.nombre,
      'Correo': winner.correo,
      'ID (Celular)': winner.id,
      'Fecha': winner.fecha
    }));

    // Crear workbook y worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ganadores');

    // Generar archivo Excel
    const fileName = `ganadores_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Función principal del sorteo
  const handleDraw = () => {
    if (pool.length === 0) {
      return;
    }

    setIsDrawing(true);
    setShowWinner(false);
    setDisplayName('');

    // Animación rápida mostrando nombres aleatorios
    let elapsed = 0;

    animationInterval.current = setInterval(() => {
      const randomUser = getRandomUser(pool);
      setDisplayName(randomUser.nombre);
      elapsed += INTERVAL_TIME;

      if (elapsed >= DRAW_DURATION) {
        clearInterval(animationInterval.current);
      }
    }, INTERVAL_TIME);

    // Después del tiempo configurado, elegir el ganador final
    drawTimeout.current = setTimeout(() => {
      clearInterval(animationInterval.current);

      // Elegir ganador final
      const winnerIndex = Math.floor(Math.random() * pool.length);
      const winner = pool[winnerIndex];

      // Actualizar estados
      setDisplayName(winner.nombre);
      setShowWinner(true);

      // Agregar ganador al array de ganadores con todos los datos
      setWinners(prevWinners => [...prevWinners, {
        numero: prevWinners.length + 1,
        nombre: winner.nombre,
        correo: winner.correo || '',
        id: winner.id || '',
        fecha: new Date().toLocaleString('es-ES')
      }]);

      // Remover ganador del pool
      const newPool = pool.filter((_, index) => index !== winnerIndex);
      setPool(newPool);

      setIsDrawing(false);
    }, DRAW_DURATION);
  };

  // Renderizado durante carga
  if (isLoading) {
    return (
      <div className="app">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Cargando participantes...</p>
          </div>
        </div>
      </div>
    );
  }

  // Renderizado en caso de error
  if (error) {
    return (
      <div className="app">
        <div className="container">
          <div className="error">
            <h2>⚠️ Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Renderizado principal
  return (
    <div className="app" style={{ backgroundImage: 'url(/bg.jpg)' }}>
      <div className="container">
        {/* Logo arriba al centro */}
        <img src="/logo.png" alt="Logo" className="top-logo" />

        {/* Autos abajo izquierda */}
        <img src="/autos.png" alt="Autos" className="bottom-left-logo" />

        {/* Hyundai abajo derecha - Botón de exportar (siempre visible) */}
        <button
          className="export-button"
          onClick={handleExportExcel}
          title={winners.length > 0 ? "Exportar ganadores a Excel" : "No hay ganadores para exportar"}
          aria-label="Exportar ganadores a Excel"
        >
          <img src="/hyundai.png" alt="Exportar" className="export-button-image" />
        </button>

        {!displayName && !isDrawing && (
          <div className="start-screen">
            <button
              className="start-button"
              onClick={handleDraw}
              disabled={pool.length === 0}
            >
              {pool.length === 0 ? 'Sorteo finalizado' : 'Iniciar Sorteo'}
            </button>
          </div>
        )}

        {displayName ? (
          <div className={`display-area ${showWinner ? 'winner vibrating' : ''}`}>
            <p className="display-name">{displayName}</p>
          </div>
        ) : isDrawing ? (
          <div className="drawing-screen">
            <p className="drawing-text">Sorteando...</p>
          </div>
        ) : null}

        {displayName && showWinner && (
          <button
            className="draw-button"
            onClick={handleDraw}
            disabled={isDrawing || pool.length === 0}
          >
            {pool.length === 0 ? 'Sorteo finalizado' : 'Siguiente Sorteo'}
          </button>
        )}
      </div>
    </div>
  );
}

export default App;
